<template>
  <div class="account-settings">
    <account-2FA v-if="getterPopupName === 'Account2FA'" :tfaValue="!tfa"
                @refreshTFA="refreshTFA" />
    <setup-2FA v-else-if="getterPopupName === 'Setup2FA'" />

    <h5>Account Settings</h5>
    <div v-if="manageAccess.accountSettings">
      <md-field>
        <label>Company Name</label>
        <div class="custom-field">
          <md-input v-model="companyName" :disabled="true" />
        </div>
      </md-field>

      <md-field class="no-right-border" :class="{'input-error': $v.gst.$error}">
        <label>GSTIN of {{backupData.company_name}}</label>
        <div class="custom-field">
          <md-input v-model="gst" :disabled="disableGst" @keyup.enter="updateGst"
                  @blur="$v.gst.$touch()" />

          <span class="input-suffix" v-if="disableGst" @click="editField('GST')"
            title="Edit">
            <i class="fa fa-pencil-alt" />
          </span>
          <span class="input-suffix" v-else>
            <i class="fa fa-times-circle" @click="cancelEdit('GST')"
              title="Cancel" />
            <i class="fa fa-check-circle" @click="updateGst"
              title="Save" />
          </span>
        </div>
      </md-field>
      <h6 class="error-message" v-if="$v.gst.$error && !$v.gst.required">Please enter a GST</h6>
      <h6 class="error-message" v-else-if="$v.gst.$error && !$v.gst.validGst">Please enter a valid GST</h6>
      <i class="info" v-if="gstStatus">Company name does not match with GST details. Please provide correct GST</i>

      <md-field class="no-right-border" :class="{'input-error': $v.address.$error}">
        <label>Billing Address</label>
        <div class="custom-field">
          <md-textarea v-model="address" :disabled="disableAddress" @blur="$v.address.$touch()" />

          <span class="textarea-suffix" v-if="disableAddress" @click="editField('Address')"
            title="Edit">
            <i class="fa fa-pencil-alt" />
          </span>
          <span class="textarea-suffix" v-else>
            <i class="fa fa-times-circle" @click="cancelEdit('Address')"
              title="Cancel" />
            <i class="fa fa-check-circle" @click="updateAddress"
              title="Save" />
          </span>
        </div>
      </md-field>
      <h6 class="error-message" v-if="$v.address.$error">Please provide Billing Address. Maximum 256 characters</h6>
    </div>
    <unprivilege-panel v-else :css="'short-height'" />

    <hr>

    <h5>Balance Settings</h5>
    <div v-if="manageAccess.accountBalance">
      <md-field class="no-border" :class="{'input-error': $v.lowBalance.$error}">
        <label>Low Balance Alert Limit</label>
        <div class="custom-field">
          <span class="input-prefix">
            <i class="fa fa-rupee-sign" />
          </span>

          <md-input v-model="lowBalance" :disabled="disableLowBalance" @keyup.enter="updateLowBalance"
                  @blur="$v.lowBalance.$touch()" :type="isDesktop() ? 'text' : 'number'"
                  :maxlength="10" />

          <span class="input-suffix" v-if="disableLowBalance" @click="editField('LowBalance')"
            title="Edit">
            <i class="fa fa-pencil-alt" />
          </span>
          <span class="input-suffix" v-else>
            <i class="fa fa-times-circle" @click="cancelEdit('LowBalance')"
              title="Cancel" />
            <i class="fa fa-check-circle" @click="updateLowBalance"
              title="Save" />
          </span>
        </div>
      </md-field>
      <h6 class="error-message" v-if="$v.lowBalance.$error && !$v.lowBalance.required">Please enter Low Balance Limit</h6>
      <h6 class="error-message" v-else-if="$v.lowBalance.$error && !$v.lowBalance.numeric">Please enter only numeric values</h6>
      <h6 class="error-message" v-else-if="$v.lowBalance.$error && !$v.lowBalance.minValue">Minimum Low Balance Limit should be &#8377; 1</h6>

      <i class="info">(Configure webhook or email alerts to receive notification)</i>
    </div>
    <unprivilege-panel v-else :css="'no-height'" />

    <hr>

    <h5>Account-Level 2FA Settings</h5>
    <div v-if="manageAccess.userWrite">
      <div class="tfa-fields">
        <p>2FA required</p>
        <md-switch v-model="tfa" @change="toggleTFA" />
      </div>
      <button class="icon-btn" @click="openPopup('Setup2FA')">
        <i class="fa fa-info-circle"></i>
        How to setup 2FA?
      </button>
    </div>
    <unprivilege-panel v-else :css="'no-height'" />
  </div>
</template>

<script>
import UnprivilegePanel from '@/components/global/UnprivilegePanel'
import Account2FA from '@/components/TFA/Account2FA'
import Setup2FA from '@/components/TFA/Setup2FA'

import ApiServices from '@/services/api/accountSettings'
import { openPopup } from '@/utils/helpers'
import { mapGetters } from 'vuex'
import { required, maxLength, minValue, numeric } from 'vuelidate/lib/validators'
import { validGst } from '@/utils/helpers/validators'

export default {
  name: 'SettingsAccount',
  components: { UnprivilegePanel, Account2FA, Setup2FA },
  data () {
    return {
      companyName: null,
      gst: null,
      address: null,
      lowBalance: null,
      tfa: false,
      gstStatus: null,
      disableGst: true,
      disableAddress: true,
      disableLowBalance: true,
      backupData: {},
      manageAccess: {
        accountSettings: true,
        accountBalance: true,
        userWrite: true
      }
    }
  },
  validations: {
    gst: {
      required,
      validGst
    },
    address: {
      required,
      maxLength: maxLength(256)
    },
    lowBalance: {
      required,
      minValue: minValue(1),
      numeric
    }
  },
  created () {
    this.populateData()
  },
  watch: {
    gst () {
      this.gst = this.gst.toUpperCase()
    }
  },
  computed: {
    ...mapGetters(['getterPopupName'])
  },
  methods: {
    openPopup,
    editField (field) {
      switch (field) {
        case 'GST' : this.disableGst = false
          break
        case 'LowBalance' : this.disableLowBalance = false
          break
        case 'Address': this.disableAddress = false
          break
      }
    },
    cancelEdit (field) {
      switch (field) {
        case 'GST' : this.disableGst = true
          this.gst = this.backupData.gstin
          this.$v.gst.$reset()
          break
        case 'LowBalance' : this.disableLowBalance = true
          this.lowBalance = this.backupData.low_balance_limit
          this.$v.lowBalance.$reset()
          break
        case 'Address': this.disableAddress = true
          this.address = this.backupData.billing_address
          this.$v.address.$reset()
          break
      }
    },
    populateData () {
      ApiServices.accountSettingsDetails().then((response) => {
        let data = response.data
        if (data) {
          let privileges = response.privileges
          this.manageAccess = {
            accountSettings: privileges.account_settings,
            accountBalance: privileges.account_balance,
            userWrite: privileges.users_write
          }

          if (privileges.account_settings) {
            this.companyName = data.company_name
            this.address = data.billing_address
            this.gst = data.gstin
            if (data.gst_status) {
              this.setGstStatus(data.gst_status)
            }
          }
          if (privileges.account_balance) {
            this.lowBalance = data.low_balance_limit
          }
          if (privileges.users_write) {
            this.tfa = data.tfa_required
          }
        }
        this.backupData = data
      })
    },
    setGstStatus (status) {
      switch (status) {
        case 'PENDING': this.gstStatus = 'GST verification is in-progress'
          break
        case 'INVALID': this.gstStatus = 'GST verification failed. Please provide a valid GST'
          break
        case 'NAME_MISMATCH': this.gstStatus = 'Company name does not match with GST details. Please provide correct GST'
          break
      }
    },
    updateGst () {
      this.$v.gst.$touch()
      if (!this.$v.gst.$error) {
        if (this.backupData.gstin !== this.gst) {
          ApiServices.updateGst({gst: this.gst}).then((response) => {
            this.handleToast(response)
            if (response.success) {
              this.disableGst = true
              let updatedData = response.data.gstin
              this.gst = updatedData
              this.backupData.gstin = updatedData

              if (response.data.gst_status) {
                this.setGstStatus(response.data.gst_status)
              }
            }
          })
        } else {
          this.disableGst = true
        }
      }
    },
    updateAddress () {
      this.$v.address.$touch()
      if (!this.$v.address.$error) {
        if (this.backupData.billing_address !== this.address) {
          ApiServices.updateAddress({address: this.address}).then((response) => {
            this.handleToast(response)
            if (response.success) {
              this.disableAddress = true
              let updatedData = response.data.billing_address
              this.address = updatedData
              this.backupData.billing_address = updatedData
            }
          })
        } else {
          this.disableAddress = true
        }
      }
    },
    updateLowBalance () {
      this.$v.lowBalance.$touch()
      if (!this.$v.lowBalance.$error) {
        if (this.backupData.low_balance_limit !== this.lowBalance) {
          ApiServices.updateLowBalance({low_balance: this.lowBalance}).then((response) => {
            this.handleToast(response)
            if (response.success) {
              this.disableLowBalance = true
              let updatedData = response.data.low_balance_limit
              this.lowBalance = updatedData
              this.backupData.low_balance_limit = updatedData
            }
          })
        } else {
          this.disableLowBalance = true
        }
      }
    },
    toggleTFA () {
      this.tfa = !this.tfa
      this.openPopup('Account2FA')
    },
    refreshTFA () {
      this.tfa = !this.tfa
      this.openPopup(null)
    },
    handleToast (response) {
      response.success ? this.successToastMsg(response.message) : this.errorToastMsg(response.message)
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/customField.scss';

  .account-settings {
    text-align: left;

    h5 {
      @include highlight-text($secondary, 18px);
      margin-bottom: 10px;
    }
    i.info {
      @include normal-text($grey-light, 13px);
    }
    hr {
      border-bottom: $divider;
      border-top: 0;
      margin: 20px 0;
    }

    .tfa-fields {
      @include custom-flex(space-between);

      p {
        @include highlight-text($secondary, 13px, 500);
      }
    }
    .icon-btn {
      margin-top: 10px;
    }
  }

  .desktop {
    .account-settings {
      .md-field, .tfa-fields {
        width: 70%
      }
    }
  }

  .tablet {
    .md-field, .tfa-fields {
      max-width: 400px;
    }
  }

  .mobile {
    label {
      word-break: break-all;
    }
  }
</style>
